import React from "react";
import { cn } from "@/lib/utils";
import { AlertCircle, Loader2, RotateCcw } from "lucide-react";
import { AvatarPortrait } from "@/components/AvatarPortrait";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { splitCoupleTurn, counsellorInitials } from "@/lib/sessionHelpers";

/**
 * A single chat message in the transcript.
 * Counsellor messages: right-aligned, sage bubble, counsellor initials avatar
 * Client messages: left-aligned, white bubble, avatar portrait
 *   - For couples, the [Name]: prefix is parsed out and shown as a label above
 *     the bubble (a single LLM reply may contain multiple speaker chunks; we
 *     render each as a separate sub-bubble inside the same row).
 */
export const ChatMessage = ({
    turn,
    avatarName,
    avatarImageUrl,
    counsellorName,
    isCouple,
    sendError,
    isSending,
    onRetry,
}) => {
    if (turn.role === "counsellor") {
        return (
            <div
                className="flex items-start gap-3 justify-end"
                data-testid={`message-counsellor-${turn._key}`}
            >
                <div className="flex max-w-[78%] flex-col items-end gap-1">
                    <div
                        className={cn(
                            "rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-sm leading-relaxed text-primary-foreground shadow-sm",
                            sendError && "ring-2 ring-destructive/60 bg-primary/80"
                        )}
                    >
                        <p className="whitespace-pre-wrap">{turn.content}</p>
                    </div>
                    {isSending && (
                        <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="counsellor-msg-sending">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            sending…
                        </span>
                    )}
                    {sendError && (
                        <button
                            onClick={onRetry}
                            className="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/10"
                            data-testid="counsellor-msg-retry"
                        >
                            <RotateCcw className="h-3 w-3" />
                            {sendError}
                        </button>
                    )}
                </div>
                <InitialsAvatar
                    name={counsellorName}
                    className="h-8 w-8 text-xs"
                    dataTestId="counsellor-avatar"
                />
            </div>
        );
    }

    // client turn
    const chunks = isCouple ? splitCoupleTurn(turn.content) : [{ speaker: null, content: turn.content }];
    return (
        <div
            className="flex items-start gap-3"
            data-testid={`message-client-${turn._key}`}
        >
            <div className="h-8 w-8 shrink-0 overflow-hidden rounded-full">
                <AvatarPortrait
                    name={avatarName}
                    imageUrl={avatarImageUrl}
                    aspect={isCouple ? "wide" : "square"}
                    rounded="rounded-full"
                />
            </div>
            <div className="flex max-w-[78%] flex-col gap-1.5">
                {chunks.map((chunk, i) => (
                    <div key={i} className="flex flex-col gap-0.5">
                        {chunk.speaker && (
                            <span className="text-xs font-medium uppercase tracking-wider text-primary/80 ml-3" data-testid={`client-speaker-${i}`}>
                                {chunk.speaker}
                            </span>
                        )}
                        <div className="rounded-2xl rounded-bl-sm bg-surface px-4 py-2.5 text-sm leading-relaxed text-foreground shadow-sm border border-border/60">
                            <p className="whitespace-pre-wrap">{chunk.content}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/** Indicator shown while waiting for the AI client to reply. */
export const TypingIndicator = ({ avatarName, avatarImageUrl, isCouple }) => (
    <div className="flex items-start gap-3" data-testid="typing-indicator">
        <div className="h-8 w-8 shrink-0 overflow-hidden rounded-full">
            <AvatarPortrait
                name={avatarName}
                imageUrl={avatarImageUrl}
                aspect={isCouple ? "wide" : "square"}
                rounded="rounded-full"
            />
        </div>
        <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-sm bg-surface px-4 py-3 shadow-sm border border-border/60">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground [animation-delay:120ms]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground [animation-delay:240ms]" />
            <span className="ml-2 text-xs text-muted-foreground">
                {(avatarName || "client").split(" & ")[0].split(" ")[0]} is typing…
            </span>
        </div>
    </div>
);

export const ConnectionBanner = ({ onRetry }) => (
    <div
        className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
        data-testid="connection-banner"
    >
        <span className="inline-flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Connection issue. Please retry.
        </span>
        <button
            onClick={onRetry}
            className="rounded-md bg-amber-200/80 px-2 py-1 text-xs font-medium hover:bg-amber-200"
            data-testid="connection-banner-retry"
        >
            Retry
        </button>
    </div>
);

// dummy export needed since counsellorInitials is referenced via imports above
export { counsellorInitials };
